import { TERAC_PAYOUT_TIMEZONE } from "./finance";

const PAID_DAY = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  timeZone: TERAC_PAYOUT_TIMEZONE,
});
const PAID_MONTH = new Intl.DateTimeFormat("en-US", {
  month: "short",
  timeZone: TERAC_PAYOUT_TIMEZONE,
});
const PAID_YEAR = new Intl.DateTimeFormat("en-US", {
  timeZone: TERAC_PAYOUT_TIMEZONE,
  year: "numeric",
});

/**
 * An instant read as one calendar day in the payout zone: "29 Aug".
 *
 * The zone is the one decision here that is not obvious. A paid date, or a
 * signature timestamp, is a true instant, so it genuinely has a local reading.
 * Two reasons it does not get one. These are finance and legal records, and the
 * moment that produced one should not become two different days depending on
 * who is looking at it. And a caller may render this on the server before it is
 * hydrated, so a viewer-zone reading of an event committed near midnight UTC
 * renders one day on the server and another in the browser -- a hydration
 * mismatch that appears only for the experts furthest from UTC.
 */
export function formatInstantDay(
  value: Date | string,
  now: Date = new Date(),
): string {
  const date = new Date(value);
  const year = PAID_YEAR.format(date);
  const label = `${PAID_DAY.format(date)} ${PAID_MONTH.format(date)}`;
  return year === PAID_YEAR.format(now) ? label : `${label} ${year}`;
}

const PAYOUT_TIME = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  timeZone: TERAC_PAYOUT_TIMEZONE,
  timeZoneName: "short",
});

/**
 * When a temporary rate hands back: "24 Sep, 12:00 AM EDT".
 *
 * The time is part of the answer here in a way it is not anywhere else on these
 * pages. `StudyExpertRate.effectiveTo` is an exclusive instant carrying a real
 * wall clock, so a rate that ends at midnight on the 24th pays nothing on the
 * 24th, and "until 24 Sep" alone reads as though it does. Printing the instant
 * makes the boundary the reader's to judge rather than ours to imply, and it is
 * what the rate board already shows an operator.
 *
 * Named rather than shown for a standing rate, which has no end to state.
 *
 * The zone is the payout zone, not the viewer's, for `formatInstantDay`'s two
 * reasons: this is a money record that should not become two different days
 * depending on who is looking, and several of these surfaces render on the
 * server, where a viewer-zone reading near midnight hydrates as a mismatch.
 * Labelled, so a reader in another zone can convert rather than guess.
 *
 * Null for anything unparseable rather than printing "Invalid Date" beside an
 * amount, which would read as a fault in the money.
 */
export function formatRateEndsAt(
  value: Date | string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return `${formatInstantDay(date, now)}, ${PAYOUT_TIME.format(date)}`;
}

/**
 * The same day, with the year always spelled out. For a record whose year is
 * part of what the reader came to check rather than noise.
 */
export function formatInstantDayWithYear(value: Date | string): string {
  const date = new Date(value);
  return `${PAID_DAY.format(date)} ${PAID_MONTH.format(date)} ${PAID_YEAR.format(date)}`;
}
